import { Component, type ErrorInfo, type ReactNode } from "react";
import { RefreshCw, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { captureException, initSentry } from "@/lib/sentry";
import { isChunkLoadError } from "@/utils/lazyWithRetry";

// Detect whether the browser/extension was auto-translating the page at crash
// time. Chrome Translate adds `translated-ltr`/`translated-rtl` to <html> and
// injects <font> wrappers — both reliable "translation was active" signals,
// and the usual cause of the `insertBefore`/`removeChild` NotFoundError.
function collectCrashContext(info: ErrorInfo): Record<string, unknown> {
  if (typeof document === "undefined") return { componentStack: info.componentStack };
  const el = document.documentElement;
  const translated =
    el.classList.contains("translated-ltr") ||
    el.classList.contains("translated-rtl") ||
    !!document.querySelector("font[style]");
  return {
    htmlLang: el.lang,
    translated,
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
    componentStack: info.componentStack,
  };
}

const RELOAD_KEY = "wl_chunk_reload";

interface FallbackProps {
  error: Error;
  resetError: () => void;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback: (props: FallbackProps) => ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Class-based error boundary. Forwards caught errors to Sentry asynchronously
 * via a lazy import so the SDK never lands in the entry chunk for public
 * visitors who don't trigger an error.
 *
 * Was previously a thin re-export of `Sentry.ErrorBoundary` which pulled the
 * full SDK into the main bundle.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Init-on-catch so even public routes (/auth) report crashes. The SDK only
    // downloads when an error actually happens, so high-traffic public pages
    // still don't pay the bundle cost on a normal visit.
    const context = collectCrashContext(info);
    void (async () => {
      await initSentry();
      await captureException(error, context);
    })();
    // Safety net: lazyWithRetry catches this at the import-promise level,
    // but if some other dynamic import (e.g. a vendor chunk loaded mid-route
    // by react-router or react-query) blows up the same way, we still
    // recover by forcing a one-shot reload here. The same sessionStorage
    // flag prevents an infinite loop.
    if (!isChunkLoadError(error)) return;
    let alreadyReloaded = false;
    try {
      alreadyReloaded = sessionStorage.getItem(RELOAD_KEY) === "1";
      if (!alreadyReloaded) sessionStorage.setItem(RELOAD_KEY, "1");
    } catch {
      // sessionStorage unavailable
    }
    if (!alreadyReloaded) {
      window.location.reload();
    }
  }

  resetError = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    if (this.state.error) {
      return this.props.fallback({ error: this.state.error, resetError: this.resetError });
    }
    return this.props.children;
  }
}

function DefaultFallback({ error, resetError }: FallbackProps) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6">
      <div className="w-full max-w-md rounded-lg border border-border bg-card p-8 text-center shadow-lg">
        <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
          <AlertTriangle className="h-6 w-6 text-destructive" />
        </div>
        <h1 className="text-xl font-semibold text-foreground">Something went wrong</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          The error has been reported. Try reloading — if it keeps happening, let us
          know and include what you were doing.
        </p>
        <pre className="mt-4 max-h-32 overflow-auto rounded bg-muted p-3 text-left text-xs text-muted-foreground">
          {error.message}
        </pre>
        <div className="mt-6 flex justify-center gap-2">
          <Button variant="outline" onClick={() => window.location.reload()}>
            Reload page
          </Button>
          <Button onClick={resetError} leftIcon={<RefreshCw />}>
            Try again
          </Button>
        </div>
      </div>
    </div>
  );
}

export { DefaultFallback };
